import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { Badge, PageLoader } from '@repo/ui';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import {
  Calendar,
  ChevronDown,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  QrCode,
  ScanLine,
  XCircle,
} from 'lucide-react';
import QRScannerModal from '../../components/qr/QRScannerModal';

const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    ('ontouchstart' in window && window.innerWidth < 1024));

type AttendanceSummary = {
  total?: number;
  present?: number;
  absent?: number;
  excused?: number;
  rate?: number;
  attendanceRate?: number;
};

type AttendanceRecord = {
  _id: string;
  status: 'present' | 'absent' | 'excused' | string;
  attendanceDate?: string;
  date?: string;
  createdAt?: string;
  sessionFormula?: string;
  session?: {
    sessionFormula?: string;
    groups?: Array<{ name?: string; code?: string }>;
  };
};

const STATUS_META: Record<
  string,
  {
    labelKey: string;
    defaultLabel: string;
    icon: typeof CheckCircle2;
    pillClass: string;
    iconClass: string;
    softClass: string;
    chartColor: string;
  }
> = {
  present: {
    labelKey: 'student.attendance.present',
    defaultLabel: 'حاضر',
    icon: CheckCircle2,
    pillClass:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    softClass:
      'bg-emerald-500/10 text-emerald-700 ring-1 ring-inset ring-emerald-500/15 dark:bg-emerald-500/15 dark:text-emerald-300',
    chartColor: '#10b981',
  },
  absent: {
    labelKey: 'student.attendance.absent',
    defaultLabel: 'غائب',
    icon: XCircle,
    pillClass:
      'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300',
    iconClass: 'text-rose-600 dark:text-rose-400',
    softClass:
      'bg-rose-500/10 text-rose-700 ring-1 ring-inset ring-rose-500/15 dark:bg-rose-500/15 dark:text-rose-300',
    chartColor: '#f43f5e',
  },
  excused: {
    labelKey: 'student.attendance.excused',
    defaultLabel: 'اعتذار',
    icon: Calendar,
    pillClass:
      'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300',
    iconClass: 'text-blue-600 dark:text-blue-400',
    softClass:
      'bg-blue-500/10 text-blue-700 ring-1 ring-inset ring-blue-500/15 dark:bg-blue-500/15 dark:text-blue-300',
    chartColor: '#3b82f6',
  },
};

const formatShortDate = (value: string | undefined, lang: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
    day: 'numeric',
    month: 'short',
  });
};

const formatTime = (value: string | undefined, lang: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getCompletionRatio = (value: number, total: number) => {
  if (!total) return 0;
  return Math.max(0, Math.min(100, Math.round((value / total) * 100)));
};

const AttendanceRateChart = ({
  rate,
  label,
}: {
  rate: number;
  label: string;
}) => {
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [rate]);

  const value = Math.max(0, Math.min(100, Number(rate || 0)));
  const data = [
    { name: 'rate', value, color: '#3559d8' },
    { name: 'rest', value: 100 - value, color: '#e9efff' },
  ];

  return (
    <div className="relative h-[188px] w-full max-w-[188px]">
      <ResponsiveContainer key={chartKey} width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={62}
            outerRadius={82}
            startAngle={90}
            endAngle={-270}
            stroke="none"
            isAnimationActive
            animationDuration={1100}
            animationEasing="ease-out"
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[34px] font-black tracking-tight text-slate-950 dark:text-white">
          {value}%
        </span>
        <span className="mt-1 text-[11px] font-bold text-slate-400 dark:text-slate-500">
          {label}
        </span>
      </div>
    </div>
  );
};

const AttendancePage = () => {
  const { t, i18n } = useTranslation();
  const [showScanner, setShowScanner] = useState(false);
  const isMobile = isMobileDevice();
  const historySectionRef = useRef<HTMLDivElement | null>(null);

  const { data: summary, isLoading: summaryLoading } = useQuery<AttendanceSummary>({
    queryKey: ['student-attendance-summary'],
    queryFn: () => api.get('/student/attendance/summary').then((res) => res.data?.data),
  });

  const { data: records, isLoading: recordsLoading } = useQuery<AttendanceRecord[]>({
    queryKey: ['student-attendance'],
    queryFn: () => api.get('/student/attendance').then((res) => res.data?.data),
  });

  if (summaryLoading || recordsLoading) {
    return <PageLoader message={t('common.loading')} />;
  }

  const attendanceRate = Math.max(
    0,
    Math.min(100, Number(summary?.attendanceRate ?? summary?.rate ?? 0))
  );

  const total = Number(summary?.total ?? 0);
  const present = Number(summary?.present ?? 0);
  const absent = Number(summary?.absent ?? 0);
  const excused = Number(summary?.excused ?? 0);
  const attendanceRecords = Array.isArray(records) ? records : [];
  const hasRecords = attendanceRecords.length > 0;
  const effectiveTotal = Math.max(total, attendanceRecords.length);
  const hasAnySessions = effectiveTotal > 0;

  const overviewCards = [
    {
      key: 'present',
      label: t('student.attendance.present', { defaultValue: 'حاضر' }),
      value: present,
      icon: CheckCircle2,
      iconClass: 'text-emerald-500',
      cardClass:
        'border-emerald-200/80 bg-emerald-50/80 dark:border-emerald-500/15 dark:bg-emerald-500/10',
    },
    {
      key: 'absent',
      label: t('student.attendance.absent', { defaultValue: 'غائب' }),
      value: absent,
      icon: XCircle,
      iconClass: 'text-rose-500',
      cardClass:
        'border-rose-200/80 bg-rose-50/80 dark:border-rose-500/15 dark:bg-rose-500/10',
    },
    {
      key: 'excused',
      label: t('student.attendance.excused', { defaultValue: 'اعتذار' }),
      value: excused,
      icon: Calendar,
      iconClass: 'text-blue-500',
      cardClass:
        'border-blue-200/80 bg-blue-50/80 dark:border-blue-500/15 dark:bg-blue-500/10',
    },
    {
      key: 'total',
      label: t('common.total'),
      value: effectiveTotal,
      icon: ClipboardCheck,
      iconClass: 'text-primary',
      cardClass:
        'border-slate-200/80 bg-slate-50/80 dark:border-white/10 dark:bg-white/[0.04]',
    },
  ];

  const statusSummary = [
    {
      key: 'present',
      label: t('student.attendance.present', { defaultValue: 'حاضر' }),
      value: present,
      colorClass: 'bg-emerald-500',
      textClass: 'text-emerald-700 dark:text-emerald-300',
    },
    {
      key: 'absent',
      label: t('student.attendance.absent', { defaultValue: 'غائب' }),
      value: absent,
      colorClass: 'bg-rose-500',
      textClass: 'text-rose-700 dark:text-rose-300',
    },
    {
      key: 'excused',
      label: t('student.attendance.excused', { defaultValue: 'اعتذار' }),
      value: excused,
      colorClass: 'bg-blue-500',
      textClass: 'text-blue-700 dark:text-blue-300',
    },
  ];

  const summaryHeadline = hasAnySessions
    ? t('student.attendance.summaryHeadline', {
        defaultValue: 'حضرت {{present}} من أصل {{total}} محاضرة حتى الآن.',
        present,
        total: effectiveTotal,
      })
    : t('student.attendance.emptyHeadline', {
        defaultValue: 'لا توجد سجلات حضور بعد.',
      });

  const summaryHint = hasAnySessions
    ? t('student.attendance.summaryHint', {
        defaultValue: 'استمر في تسجيل حضورك بانتظام للحفاظ على معدل التزام مرتفع.',
      })
    : t('student.attendance.emptyHint', {
        defaultValue: 'بعد أول تسجيل حضور ستظهر هنا النسبة، الملخص، وآخر الجلسات.',
      });

  const scrollToHistory = () => {
    historySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      {showScanner && <QRScannerModal onClose={() => setShowScanner(false)} />}

      {isMobile && (
        <div className="fixed bottom-[24px] left-1/2 z-40 flex -translate-x-1/2 flex-col items-center gap-1.5">
          <button
            onClick={() => setShowScanner(true)}
            className="flex h-[58px] w-[58px] items-center justify-center rounded-full border-[3px] border-white/30 bg-primary shadow-[0_8px_24px_-4px_rgba(var(--primary-rgb),0.6)] transition-all active:scale-95 hover:bg-primary/90"
            style={{
              animation: 'qrpulse 1.8s ease-in-out infinite',
              boxShadow: '0 8px 24px -4px hsla(var(--primary), 0.6)',
            }}
          >
            <div className="flex h-[42px] w-[42px] items-center justify-center rounded-full bg-white/15">
              <QrCode className="h-6 w-6 text-white" strokeWidth={2} />
            </div>
          </button>
          <span className="text-[10px] font-black leading-none text-primary drop-shadow-sm">
            {t('student.attendance.checkIn', { defaultValue: 'سجل حضوري' })}
          </span>
        </div>
      )}

      <section className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-950 md:p-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-center">
          <div className="hidden justify-center lg:order-2 lg:flex lg:justify-end">
            <AttendanceRateChart
              rate={attendanceRate}
              label={t('student.attendance.rate', { defaultValue: 'نسبة الحضور' })}
            />
          </div>

          <div className="order-1 space-y-5 lg:order-1">
            <div className="space-y-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/8 ring-1 ring-inset ring-primary/10">
                <ClipboardCheck className="h-5 w-5 text-primary" strokeWidth={2.4} />
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-primary/65">
                  {t('student.attendance.rate', { defaultValue: 'نسبة الحضور' })}
                </p>
                <h1 className="text-[28px] font-black tracking-tight text-slate-950 dark:text-white md:text-[42px]">
                  {t('student.attendance.title')}
                </h1>
                <p className="max-w-xl text-sm font-bold leading-6 text-slate-500 dark:text-slate-400">
                  {summaryHeadline}
                </p>
                <p className="max-w-2xl text-sm font-medium leading-6 text-slate-500 dark:text-slate-400">
                  {summaryHint}
                </p>
              </div>
            </div>

            <div className="flex justify-center lg:hidden">
              <AttendanceRateChart
                rate={attendanceRate}
                label={t('student.attendance.rate', { defaultValue: 'نسبة الحضور' })}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 text-xs font-bold text-slate-500 dark:text-slate-400">
              <span className="rounded-full bg-primary/8 px-3 py-1.5 text-primary">
                {t('student.attendance.rate', { defaultValue: 'نسبة الحضور' })}: {attendanceRate}%
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1.5 dark:bg-white/5">
                {t('student.attendance.sessions', { defaultValue: 'محاضرات' })}: {effectiveTotal}
              </span>
              {absent > 0 && (
                <span className="rounded-full bg-rose-50 px-3 py-1.5 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300">
                  {t('student.attendance.absent', { defaultValue: 'غائب' })}: {absent}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowScanner(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-black text-white transition-colors hover:bg-primary/90"
              >
                <ScanLine className="h-4 w-4" strokeWidth={2.4} />
                {t('student.attendance.checkIn', { defaultValue: 'سجل حضوري' })}
              </button>
              <button
                type="button"
                onClick={scrollToHistory}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition-colors hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-transparent dark:text-slate-200"
              >
                <ChevronDown className="h-4 w-4" strokeWidth={2.6} />
                {t('student.attendance.viewHistory', { defaultValue: 'عرض السجل' })}
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {overviewCards.map((card) => {
                const Icon = card.icon;

                return (
                  <div
                    key={card.key}
                    className={`rounded-[20px] border p-4 transition-colors ${card.cardClass}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-black text-muted-foreground/70">
                          {card.label}
                        </p>
                        <p className="mt-2 text-[26px] font-black leading-none text-foreground">
                          {card.value}
                        </p>
                      </div>
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/80 ring-1 ring-inset ring-black/5 dark:bg-white/5 dark:ring-white/10">
                        <Icon className={`h-5 w-5 ${card.iconClass}`} strokeWidth={2.3} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="rounded-[28px] border border-slate-200/70 bg-card p-4 shadow-sm md:p-5">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-black tracking-tight text-foreground">
                <ClipboardCheck className="h-5 w-5 text-primary" strokeWidth={2.3} />
                {t('student.attendance.overviewTitle', { defaultValue: 'ملخص الحضور' })}
              </h2>
              <p className="mt-1 text-xs font-bold text-muted-foreground/70">
                {t('student.attendance.overviewSub', {
                  defaultValue: 'توزيع حالات الحضور عبر كل المحاضرات المسجلة.',
                })}
              </p>
            </div>
            <Badge className="rounded-full border-transparent bg-primary/10 px-3 py-1 text-primary hover:bg-primary/10">
              {attendanceRate}%
            </Badge>
          </div>

          {hasAnySessions ? (
            <div className="space-y-4">
              {statusSummary.map((item) => (
                <div key={item.key} className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${item.colorClass}`} />
                      <p className="text-sm font-black text-foreground">{item.label}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-black ${item.textClass}`}>{item.value}</span>
                      <span className="text-xs font-bold text-muted-foreground/70">
                        {getCompletionRatio(item.value, effectiveTotal)}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                    <div
                      className={`h-full rounded-full ${item.colorClass}`}
                      style={{ width: `${getCompletionRatio(item.value, effectiveTotal)}%` }}
                    />
                  </div>
                </div>
              ))}

              <div className="rounded-[24px] border border-slate-200/70 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.03]">
                <p className="text-sm font-black text-foreground">
                  {t('student.attendance.summaryCardTitle', { defaultValue: 'نظرة سريعة' })}
                </p>
                <p className="mt-2 text-sm font-medium leading-6 text-muted-foreground">
                  {summaryHeadline}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[24px] border border-dashed border-border bg-muted/20 px-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <ClipboardCheck className="h-6 w-6" strokeWidth={2.3} />
              </div>
              <p className="mt-4 text-base font-black text-foreground">
                {t('student.attendance.emptyTitle', { defaultValue: 'لا توجد بيانات حضور بعد' })}
              </p>
              <p className="mt-2 max-w-md text-sm font-medium leading-6 text-muted-foreground">
                {t('student.attendance.emptyHint', {
                  defaultValue: 'بعد أول تسجيل حضور ستظهر هنا النسبة، الملخص، وآخر الجلسات.',
                })}
              </p>
            </div>
          )}
        </div>

        <div
          id="attendance-history"
          ref={historySectionRef}
          className="rounded-[28px] border border-slate-200/70 bg-card p-4 shadow-sm md:p-5"
        >
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-black tracking-tight text-foreground">
                {t('student.attendance.historyTitle', { defaultValue: 'آخر سجلات الحضور' })}
              </h2>
              <p className="mt-1 text-xs font-bold text-muted-foreground/70">
                {t('student.attendance.historySub', {
                  defaultValue: 'آخر المحاضرات التي تم تسجيل حالتها لك.',
                })}
              </p>
            </div>
          </div>

          {hasRecords ? (
            <div className="space-y-3">
              {attendanceRecords.slice(0, 5).map((record) => {
                const stamp = record.attendanceDate || record.date || record.createdAt;
                const statusMeta = STATUS_META[record.status] || STATUS_META.present;
                const Icon = statusMeta.icon;
                const sessionGroups = record.session?.groups || [];
                const groupName =
                  record.sessionFormula ||
                  record.session?.sessionFormula ||
                  sessionGroups[0]?.code ||
                  sessionGroups[0]?.name ||
                  t('common.generalSession', { defaultValue: 'General Session' });

                return (
                  <article
                    key={record._id}
                    className="flex items-center justify-between gap-3 rounded-[22px] border border-border/70 bg-background/80 p-3 transition-all duration-300 hover:border-primary/20 hover:shadow-sm dark:bg-background/20"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${statusMeta.softClass}`}
                      >
                        <Icon className={`h-5 w-5 ${statusMeta.iconClass}`} strokeWidth={2.4} />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-foreground">{groupName}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] font-bold text-muted-foreground/75">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5 text-primary/70" />
                            {formatShortDate(stamp, i18n.language)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5 text-primary/70" />
                            {formatTime(stamp, i18n.language)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <Badge className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-black ${statusMeta.pillClass}`}>
                      {t(statusMeta.labelKey, { defaultValue: statusMeta.defaultLabel })}
                    </Badge>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[24px] border border-dashed border-border bg-muted/20 px-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Calendar className="h-6 w-6" strokeWidth={2.3} />
              </div>
              <p className="mt-4 text-base font-black text-foreground">
                {t('student.attendance.noRecords', { defaultValue: 'لا توجد سجلات حضور حتى الآن' })}
              </p>
              <p className="mt-2 max-w-md text-sm font-medium leading-6 text-muted-foreground">
                {t('student.attendance.historyEmptyHint', {
                  defaultValue: 'عند تسجيل حضورك في أي محاضرة، ستظهر أحدث السجلات هنا بالتاريخ والحالة.',
                })}
              </p>
              <button
                onClick={() => setShowScanner(true)}
                className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-2.5 text-sm font-black text-primary transition-colors hover:bg-primary/10"
              >
                <QrCode className="h-4 w-4" strokeWidth={2.3} />
                {t('student.attendance.checkIn', { defaultValue: 'سجل حضوري' })}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AttendancePage;
